// FHIR
import Client from "fhir-kit-client";
import type { Practitioner, PractitionerRole, Bundle, Slot, Schedule } from "fhir/r5";
import moment from "moment";
// Data
import { scheduleCategory } from "../utils/ScheduleCategory";
import { getDisabledDates } from "../utils/DisabledDate";

/////////////////////////////////
//        TYPES                //
/////////////////////////////////

export type Group = {
    id: string;
    title: string;
};

export type PractitionerRolesResult = {
    groups: Group[];
    error: boolean;
};

export type Item = {
    id: string;
    group: string;
    title: string;
    start_time: number;
    end_time: number;
    className?: string;
};

/**
 * Service class Calendar : Manages FHIR resources
 * Add Practitioner on calendar by PractitionerRole
 */
class CalendarService {
    private fhirClient: Client;

  ////////////////////////////////
  //        CONSTRUCTOR         //
  ////////////////////////////////

    /**
     * Initialize FHIR client with environnement URL
     */
    constructor() {
        const baseURL = process.env.REACT_APP_FHIR_URL;
        this.fhirClient = new Client({
            baseUrl: baseURL ?? "fhir",
        });
    }

    ////////////////////////////////
    //         FUNCTIONS          //
    ////////////////////////////////

    /**
     * Loads PractitionerRole and associated Practitioner
     * @returns Promise for calendar Display
     */
    async loadPractitionerRoles(): Promise<Group[]> {
        return (await this.loadPractitionerRolesResult()).groups;
    }

    async loadPractitionerRolesResult(): Promise<PractitionerRolesResult> {
        try {
            const response = await this.fhirClient.search({
                resourceType: 'PractitionerRole',
                searchParams: {
                    "_include": "PractitionerRole:practitioner"
                },
            });
            const bundle = response as Bundle;
            const freshGroups: Group[] = [];
            // Process each entry in the bundle
            bundle.entry?.forEach((entry) => {
                if (entry.resource?.resourceType === "PractitionerRole") {
                    const practitionerRole = entry.resource as PractitionerRole;
                    //Find the Practitioner ID
                    const practitionerId = practitionerRole.practitioner?.reference?.split('/').pop();
                    //Find resources corresponding to the Practitioner
                    const practitioner = bundle.entry?.find(e =>
                        e.resource?.resourceType === "Practitioner" &&
                        e.resource.id === practitionerId
                    )?.resource as Practitioner;
                    const id = practitionerRole.id;
                    if (!id) return;
                    const family = practitioner?.name?.[0]?.family || "";
                    const given = practitioner?.name?.[0]?.given?.join(" ") || "";
                    const fullName = `${given} ${family}`.trim()
                        || practitionerRole.practitioner?.display
                        || practitionerId
                        || "Unknown Practitioner";
                    // Keep the role visible even when the server omits the included Practitioner.
                    freshGroups.push({ id, title: fullName });
                }
            });
            return { groups: freshGroups, error: false };
        } catch (error) {
            return { groups: [], error: true };
        }
    }

    /**
    * Load Schedule and slots for all Practitioners
    * @param agents Array of practitioner groups
    * @returns Promise item array of calendar items
    */
    async loadSchedule(agents: Group[]): Promise<Item[]> {
        const freshItems: Item[] = [];
        // Get Practitioner's Schedule
        await Promise.all(agents.map(async (agent) => {
            try {  
                //Search for Schedule resources for this practitioner and include related Slots
                const response = await this.fhirClient.search({
                    resourceType: 'Schedule',
                    searchParams: {
                        actor: `PractitionerRole/${agent.id}`,
                        _revinclude: "Slot:schedule"
                    },
                });
                const bundle = response as Bundle;
                //Process each schedule entry
                bundle.entry?.forEach((entry) => {
                    if (entry.resource?.resourceType !== "Schedule") return;
                    const schedule = entry.resource as Schedule;                 
                    //Find all slots associated with this schedule                   
                    const slots = bundle.entry?.filter(
                        e => e.resource?.resourceType === "Slot" &&
                            e.resource?.schedule?.reference === `Schedule/${schedule.id}`
                    );                   
                    //Process each slot               
                    slots?.forEach((slotEntry) => {
                        const slotResource = slotEntry.resource as Slot;
                        const start_time = slotResource.start ? new Date(slotResource.start).getTime() : 0;
                        const end_time = slotResource.end ? new Date(slotResource.end).getTime() : 0;
                        const categoryCode = slotResource.serviceCategory?.[0]?.coding?.[0]?.code || "unknown";                        
                        //Find matching category configuration or use default                       
                        const categoryClass = scheduleCategory.find(
                            cat => cat.code.toUpperCase() === categoryCode.toUpperCase()
                        ) || {
                            code: "Unknown",
                            displayKey: "Unknown",
                            abbreviation: "?",
                            color: "#ccc",
                            className: "item-grey"
                        };
                        if (!agent.id) return;
                        //Add slot as calendar item
                        freshItems.push({
                            id: slotResource.id ?? "",
                            group: agent.id,
                            title: categoryClass.abbreviation,
                            start_time,
                            end_time,
                            className: `item-${categoryClass.code.toLowerCase()}`,
                        });
                    });
                });
            } catch (error) {
            }
        }));

        /////////////////////////////////
        //        CONSTANTS            //
        /////////////////////////////////

        // Generate disabled dates (holidays and weekends)
        const currentYear = new Date().getFullYear();

        const disabledDates = getDisabledDates(currentYear).Disabled.map(d => d.toDateString());

        // Generate all days of the current year
        const daysOfYear: Date[] = [];

        for (let d = new Date(currentYear, 0, 1); d.getFullYear() === currentYear; d.setDate(d.getDate() + 1)) {
            daysOfYear.push(new Date(d));
        }
        
        // Create a set of dates that have slots for each Practitioner
        const slotDatesSet = new Set(freshItems.map((item: any) => new Date(item.start_time).toDateString()));

        // Add disabled items for holidays, weekends, and days without Slots
        daysOfYear.forEach(date => {
            const dateStr = date.toDateString();
            const isDisabled = disabledDates.includes(dateStr);
            const hasNoSlot = !slotDatesSet.has(dateStr);
            //If day is disabled or has no slots, add disabled item for each Practitioner
            if (isDisabled || hasNoSlot) {
                agents.forEach(agent => {
                    freshItems.push({
                        id: `disabled-${agent.id}-${dateStr}`,
                        group: agent.id,
                        title: "",
                        start_time: moment(date.setHours(0, 0, 0, 0)).valueOf(),
                        end_time: moment(date.setHours(23, 59, 59, 999)).valueOf(),
                        className: "item-disabled"
                    });
                });
            }
        });
        return freshItems;
    }
}

export default CalendarService;
